import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faCalendarDay, faMoneyBillWave, faPenToSquare, faPlus, faTrashCan, faWallet } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../../Pages/dash.css';
import {
  formatKes,
  formatShortDate,
  getMonthKey,
  getMonthLabel,
  summarizeFixedBills
} from '../../utils/financeWorkflow';

const BILL_CYCLE_OPTIONS = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual', 'Daily'];

const buildEmptyForm = () => ({
  vendor: '',
  billTitle: '',
  billAmount: '',
  billCycle: 'Monthly',
  billDueDate: ''
});

function BillsComp() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [detailsModalShow, setDetailsModalShow] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [formMode, setFormMode] = useState('add');
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState('All');
  const [cycleFilter, setCycleFilter] = useState('All');
  const [formState, setFormState] = useState(buildEmptyForm());

  const loadPage = async () => {
    const [vendorSnapshot, branchSnapshot, billSnapshot, paymentSnapshot] = await Promise.all([
      getDocs(collection(db, 'Vendors')),
      getDocs(collection(db, 'Branches')),
      getDocs(collection(db, 'Bills')),
      getDocs(collection(db, 'BillPayments'))
    ]);

    setVendors(
      vendorSnapshot.docs.map((vendorDoc) => ({
        id: vendorDoc.id,
        ...vendorDoc.data()
      }))
    );
    setBranches(
      branchSnapshot.docs.map((branchDoc) => ({
        id: branchDoc.id,
        ...branchDoc.data()
      }))
    );
    setBills(
      billSnapshot.docs.map((billDoc) => ({
        id: billDoc.id,
        ...billDoc.data()
      }))
    );
    setPayments(
      paymentSnapshot.docs.map((paymentDoc) => ({
        id: paymentDoc.id,
        ...paymentDoc.data()
      }))
    );
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load fixed bills:', error);
        swal('Error', 'Fixed bills could not be loaded right now.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, []);

  useEffect(() => {
    if (vendors.length === 0) {
      return;
    }

    setFormState((currentForm) => (
      currentForm.vendor
        ? currentForm
        : {
            ...currentForm,
            vendor: vendors[0]?.id || ''
          }
    ));
  }, [vendors]);

  const vendorsById = useMemo(() => {
    return vendors.reduce((accumulator, vendor) => {
      accumulator[vendor.id] = vendor;
      return accumulator;
    }, {});
  }, [vendors]);

  const branchMap = useMemo(() => {
    return branches.reduce((accumulator, branch) => {
      accumulator[branch.id] = branch.branchName;
      return accumulator;
    }, {});
  }, [branches]);

  const billRows = useMemo(() => {
    return summarizeFixedBills({
      bills,
      vendorsById,
      branchesById: branchMap,
      payments,
      periodKey
    }).sort((first, second) => {
      if (second.remainingAmount !== first.remainingAmount) {
        return second.remainingAmount - first.remainingAmount;
      }

      return second.expectedAmount - first.expectedAmount;
    });
  }, [bills, branchMap, payments, periodKey, vendorsById]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return billRows.filter((bill) => {
      const matchesBranch =
        branchFilter === 'All' ||
        (branchFilter === 'General' && bill.branchId === 'General') ||
        bill.branchId === branchFilter;
      const matchesCycle = cycleFilter === 'All' || bill.billCycle === cycleFilter;

      if (!matchesBranch || !matchesCycle) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const searchBlob = [
        bill.billTitle,
        bill.vendorName,
        bill.branchName,
        bill.vendorServices
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchBlob.includes(normalizedSearch);
    });
  }, [billRows, branchFilter, cycleFilter, searchTerm]);

  const summary = useMemo(() => {
    return billRows.reduce((accumulator, bill) => {
      accumulator.expected += bill.expectedAmount;
      accumulator.paid += bill.paidAmount;
      accumulator.remaining += bill.remainingAmount;
      if (bill.remainingAmount <= 0 && bill.expectedAmount > 0) {
        accumulator.settled += 1;
      } else if (bill.paidAmount > 0) {
        accumulator.partial += 1;
      } else {
        accumulator.pending += 1;
      }
      return accumulator;
    }, {
      expected: 0,
      paid: 0,
      remaining: 0,
      settled: 0,
      partial: 0,
      pending: 0
    });
  }, [billRows]);

  const closeModal = () => {
    if (submitting) {
      return;
    }

    setModalShow(false);
  };

  const resetForm = () => {
    setSelectedBill(null);
    setFormMode('add');
    setFormState({
      ...buildEmptyForm(),
      vendor: vendors[0]?.id || ''
    });
  };

  const openCreateModal = () => {
    setFormMode('add');
    setSelectedBill(null);
    setFormState({
      ...buildEmptyForm(),
      vendor: vendors[0]?.id || ''
    });
    setModalShow(true);
  };

  const openEditModal = (bill) => {
    setFormMode('edit');
    setSelectedBill(bill);
    setFormState({
      vendor: bill.vendor || vendors[0]?.id || '',
      billTitle: bill.billTitle || '',
      billAmount: String(bill.billAmount || ''),
      billCycle: bill.billCycle || 'Monthly',
      billDueDate: String(bill.billDueDate || '')
    });
    setModalShow(true);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormState((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const handleSubmitBill = async () => {
    const amountValue = Number(formState.billAmount);
    const dueDayValue = Number(formState.billDueDate);

    if (!formState.vendor || !formState.billTitle.trim()) {
      swal('Missing details', 'Please choose a vendor and enter a bill title.', 'warning');
      return;
    }

    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Bill amount should be greater than zero.', 'warning');
      return;
    }

    if (!Number.isFinite(dueDayValue) || dueDayValue < 1 || dueDayValue > 31) {
      swal('Invalid due day', 'Due day should be a number between 1 and 31.', 'warning');
      return;
    }

    setSubmitting(true);

    try {
      const billRef = formMode === 'edit' && selectedBill?.id
        ? doc(db, 'Bills', selectedBill.id)
        : doc(collection(db, 'Bills'));
      await setDoc(billRef, {
        vendor: formState.vendor,
        billTitle: formState.billTitle.trim(),
        billAmount: amountValue,
        billCycle: formState.billCycle,
        billDueDate: dueDayValue,
        billID: selectedBill?.billID || billRef.id,
        timeStamp: selectedBill?.timeStamp || serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      swal('Success', formMode === 'edit' ? 'Fixed bill updated successfully.' : 'Fixed bill added successfully.', 'success');
      setModalShow(false);
      resetForm();
      await loadPage();
    } catch (error) {
      console.error('Failed to save fixed bill:', error);
      swal('Error', 'Fixed bill could not be saved right now.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBill = async (bill) => {
    const linkedPayments = payments.filter((payment) => payment.billId === bill.id);
    const confirmationMessage = linkedPayments.length > 0
      ? `Delete ${bill.billTitle} and its ${linkedPayments.length} linked payment record${linkedPayments.length === 1 ? '' : 's'}? This cannot be undone.`
      : `Delete ${bill.billTitle}? This cannot be undone.`;

    const shouldDelete = await swal({
      title: 'Delete fixed bill?',
      text: confirmationMessage,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true
    });

    if (!shouldDelete) {
      return;
    }

    try {
      await Promise.all([
        deleteDoc(doc(db, 'Bills', bill.id)),
        ...linkedPayments.map((payment) => deleteDoc(doc(db, 'BillPayments', payment.id)))
      ]);

      if (selectedBill?.id === bill.id) {
        setSelectedBill(null);
        setDetailsModalShow(false);
      }

      swal('Deleted', 'Fixed bill removed successfully.', 'success');
      await loadPage();
    } catch (error) {
      console.error('Failed to delete fixed bill:', error);
      swal('Error', 'Fixed bill could not be deleted right now.', 'error');
    }
  };

  return (
    <div className="teamsComponent opsWorkspace opsWorkspace--framed fixedBillsPage">
      <section className="opsHero fixedBillsHero">
        <div className="opsHeroCopy">
          <span className="opsEyebrow">Finance workflow</span>
          <h3>Fixed bills</h3>
          <p>
            Keep recurrent costs tidy, review what is due for the month, and manage each bill from a
            cleaner operations view.
          </p>
        </div>
        <button type="button" className="opsPrimaryBtn fixedBillsPrimaryBtn" onClick={openCreateModal}>
          <FontAwesomeIcon icon={faPlus} /> Add fixed bill
        </button>
      </section>

      <section className="opsStatsGrid fixedBillsStatsGrid">
        <article className="opsStatCard fixedBillsStatCard">
          <span>{getMonthLabel(periodKey)} expected</span>
          <strong>{formatKes(summary.expected)}</strong>
        </article>
        <article className="opsStatCard fixedBillsStatCard">
          <span>Paid this period</span>
          <strong>{formatKes(summary.paid)}</strong>
        </article>
        <article className="opsStatCard fixedBillsStatCard">
          <span>Outstanding</span>
          <strong>{formatKes(summary.remaining)}</strong>
        </article>
        <article className="opsStatCard fixedBillsStatCard">
          <span>Settled / partial / pending</span>
          <strong>{summary.settled} / {summary.partial} / {summary.pending}</strong>
        </article>
      </section>

      <section className="opsPanel fixedBillsPanel">
        <div className="opsToolbar fixedBillsToolbar">
          <div>
            <h4>Bill schedule</h4>
            <p>Review each vendor bill by month, then open it when you are ready to work with payments.</p>
          </div>
          <div className="opsFilters">
            <Form.Control
              type="month"
              value={periodKey}
              onChange={(event) => setPeriodKey(event.target.value)}
            />
            <Form.Control
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search bill or vendor..."
            />
            <Form.Select value={branchFilter} onChange={(event) => setBranchFilter(event.target.value)}>
              <option value="All">All campuses</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.branchName}
                </option>
              ))}
              <option value="General">General</option>
            </Form.Select>
            <Form.Select value={cycleFilter} onChange={(event) => setCycleFilter(event.target.value)}>
              <option value="All">All cycles</option>
              {BILL_CYCLE_OPTIONS.map((cycle) => (
                <option key={cycle} value={cycle}>
                  {cycle}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        {loading ? (
          <div className="opsLoadingCard">
            <Spinner animation="border" role="status" />
            <span>Loading fixed bills...</span>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="opsEmptyState">
            <h6>No fixed bills found</h6>
            <p>Try another filter or add the first recurrent bill for this workflow.</p>
          </div>
        ) : (
          <div className="opsTableWrap">
            <table className="opsTable">
              <thead>
                <tr>
                  <th>Bill</th>
                  <th>Campus</th>
                  <th>Cycle</th>
                  <th>Due rule</th>
                  <th>Expected</th>
                  <th>Paid</th>
                  <th>Remaining</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((bill) => (
                  <tr key={bill.id}>
                      <td data-label="Bill">
                        <div className="opsDetailStack">
                          <strong>{bill.billTitle}</strong>
                          <small>{bill.vendorName}</small>
                        </div>
                      </td>
                      <td data-label="Campus">{bill.branchName}</td>
                      <td data-label="Cycle">{bill.billCycle || 'Monthly'}</td>
                      <td data-label="Due rule">Day {bill.billDueDate || '—'} of cycle</td>
                      <td data-label="Expected">{formatKes(bill.expectedAmount)}</td>
                      <td data-label="Paid">
                        <div className="opsDetailStack">
                          <strong>{formatKes(bill.paidAmount)}</strong>
                          <small>{bill.paymentCount} payment{bill.paymentCount === 1 ? '' : 's'}</small>
                        </div>
                      </td>
                      <td data-label="Remaining">{formatKes(bill.remainingAmount)}</td>
                      <td data-label="Status">
                        <span className={`opsStatusPill opsStatusPill--${bill.paymentTone}`}>
                          {bill.paymentStatus}
                        </span>
                      </td>
                      <td data-label="Action">
                        <div className="opsActionRow fixedBillsActionRow">
                          <button
                            type="button"
                            className="opsGhostBtn"
                            onClick={() => {
                              setSelectedBill(bill);
                              setDetailsModalShow(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faWallet} /> Vendor
                          </button>
                          <button
                            type="button"
                            className="opsGhostBtn"
                            onClick={() => openEditModal(bill)}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} /> Edit
                          </button>
                          <button
                            type="button"
                            className="opsGhostBtn"
                            onClick={() => navigate(`/view-fixed-bill/${bill.id}`)}
                          >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Open bill
                          </button>
                          <button
                            type="button"
                            className="opsGhostBtn opsGhostBtn--danger"
                            onClick={() => handleDeleteBill(bill)}
                          >
                            <FontAwesomeIcon icon={faTrashCan} /> Delete
                          </button>
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal show={modalShow} onHide={closeModal} centered>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>{formMode === 'edit' ? 'Edit fixed bill' : 'Add fixed bill'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Vendor</Form.Label>
            <Form.Select name="vendor" value={formState.vendor} onChange={handleFormChange}>
              {vendors.map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.vendorName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Bill title</Form.Label>
            <Form.Control
              name="billTitle"
              value={formState.billTitle}
              onChange={handleFormChange}
              placeholder="e.g. Rent, Internet, Water"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Expected amount</Form.Label>
            <Form.Control
              type="number"
              name="billAmount"
              value={formState.billAmount}
              onChange={handleFormChange}
              placeholder="100000"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Cycle</Form.Label>
            <Form.Select name="billCycle" value={formState.billCycle} onChange={handleFormChange}>
              {BILL_CYCLE_OPTIONS.map((cycle) => (
                <option key={cycle} value={cycle}>
                  {cycle}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-0">
            <Form.Label>Due day in the cycle</Form.Label>
            <Form.Control
              type="number"
              name="billDueDate"
              value={formState.billDueDate}
              onChange={handleFormChange}
              placeholder="5"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={closeModal} disabled={submitting}>
            Close
          </Button>
          <Button variant="dark" onClick={handleSubmitBill} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" /> Saving...
              </>
            ) : (
              formMode === 'edit' ? 'Save changes' : 'Save fixed bill'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={detailsModalShow} onHide={() => setDetailsModalShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Vendor and billing notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBill ? (
            <div className="opsModalPreview">
              <div className="opsModalPreviewRow">
                <span>
                  <FontAwesomeIcon icon={faMoneyBillWave} /> Bill
                </span>
                <strong>{selectedBill.billTitle}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Vendor</span>
                <strong>{selectedBill.vendorName}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Campus</span>
                <strong>{selectedBill.branchName}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Cycle</span>
                <strong>{selectedBill.billCycle || 'Monthly'}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>Expected amount</span>
                <strong>{formatKes(selectedBill.expectedAmount)}</strong>
              </div>
              <div className="opsModalPreviewRow">
                <span>
                  <FontAwesomeIcon icon={faCalendarDay} /> Last payment
                </span>
                <strong>{formatShortDate(selectedBill.lastPaymentDate, 'No payment yet')}</strong>
              </div>
              <div className="opsModalCopyBlock">
                <span>Service notes</span>
                <p>{selectedBill.serviceDesc || 'No service notes added yet.'}</p>
              </div>
              <div className="opsModalCopyBlock">
                <span>Payment details</span>
                <p>{selectedBill.paymentDetails || 'No payment details added yet.'}</p>
              </div>
              <div className="opsModalCopyBlock">
                <span>Contact</span>
                <p>{selectedBill.vendorPhone || selectedBill.vendorEmail || 'No contact details added yet.'}</p>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={() => handleDeleteBill(selectedBill)}>
            Delete bill
          </Button>
          <Button variant="outline-dark" onClick={() => setDetailsModalShow(false)}>
            Close
          </Button>
          <Button variant="dark" onClick={() => {
            setDetailsModalShow(false);
            openEditModal(selectedBill);
          }}>
            Edit bill
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default BillsComp;
